import { useQuery, type QueryClient } from "@tanstack/react-query";
import type { Params } from "react-router";
import axios from "@/utils/axios";
import type { AxiosRequestConfig } from "axios";
import type { RequestFormResponse } from "@/types/common/form";
import type {
  ApiFormalityFormResponseType,
  FormDetailResponse,
} from "@/types/entities/formality-request";

export const useGetTramiteForms = (
  tramiteId: string,
  params: {
    page: number;
    per_page: number;
  },
  refreshInterval = 0,
) => {
  return useQuery<ApiFormalityFormResponseType>({
    queryKey: ["tramite-forms", tramiteId, params],
    queryFn: async (): Promise<ApiFormalityFormResponseType> => {
      const response = await axios.get(`/admin/v1/forms`, {
        params: {
          ...params,
          "filter[formality]": tramiteId,
          relationships: "formAssignments",
        },
      });
      return response.data;
    },
    ...(refreshInterval > 0 ? { refetchInterval: refreshInterval } : {}),
    enabled: !!tramiteId, // Solo ejecutar si hay tramiteId
  });
};

const getFormDetails = async (
  id: string,
  formality: string,
): Promise<FormDetailResponse> => {
  const response = await axios.get(`/admin/v1/forms/${id}/v2`, {
    params: {
      "filter[formality]": formality,
    },
  });
  return response.data.data;
};

//* This should not exist, but there is not way to apply eager loading without refactoring all Form API endpoints with Frontend integrations
export const useGetFormDetail2 = (formId: string, formality: string) => {
  return useQuery<FormDetailResponse>({
    queryKey: ["form-detail", formId],
    queryFn: async (): Promise<FormDetailResponse> => {
      const response = await axios.get(`/admin/v1/forms/${formId}/v2`, {
        params: {
          relationships:
            "steps.sections.fields.filledFields,formAssignments,userFormAssignments,userFormAssignments.user,userFormAssignments.filledForm,userFormAssignments.form,formAssignments.revisions.corrections.fieldCorrections.filledField",
          "filter[formality]": formality,
        },
      });
      return response.data.data;
    },
    enabled: !!formId && !!formality,
  });
};

// Para cargar variables de otros nodos
export const useGetFormByAssignmentId = (
  assignmentId: string,
  options?: { skip404Redirect?: boolean },
) => {
  return useQuery({
    queryKey: ["get-form-by-assignment-id", assignmentId],
    queryFn: async (): Promise<RequestFormResponse> => {
      const requestConfig: AxiosRequestConfig & {
        metadata?: { skip403Redirect?: boolean; skip404Redirect?: boolean };
      } = {
        metadata: {
          skip403Redirect: true,
          skip404Redirect: options?.skip404Redirect ?? false,
        },
      };

      const { data } = await axios.get(
        `/admin/v1/internal/forms/assignments/${assignmentId}`,
        requestConfig,
      );
      return {
        data: data.data,
      };
    },
    enabled: !!assignmentId && assignmentId !== "",
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
};

export const createGetFormDetailBreadcrumb = (
  queryClient: QueryClient,
  formId: string,
  formality: string,
  params?: Params<string>,
) => {
  return async () => {
    try {
      const formDetail = await queryClient.ensureQueryData({
        queryKey: ["form-detail", formId],
        queryFn: async () => {
          const response = await getFormDetails(formId, formality);
          return response;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
      });
      return {
        data: {
          name: formDetail.name,
          params,
        },
      };
    } catch (error) {
      console.log("Error fetching form for breadcrumb:", error);
      return {
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  };
};
